import React, { useEffect, useRef, useState } from 'react';
import { ErrorInput } from '@fx-ui/jdy-design-biz';
import { VALID_STEP, VALID_TYPE } from '../../interfaces';
import { ECode, EMPTY_FN, get, includes, isEmpty, isNil, map, size, trim } from '@libs/shared';
import { Button, Dialog, Drawer, Input, Message } from '@fx-ui/fine-design';
import { platformIntl, TEXT_TYPE_REGEXP, UGC_MAX_LENGTH, API } from '../../constants';
import { getEmail, getPhone } from '../../apis';
import CaptchaDialog from '../captcha-dialog';
import { I18nPhone, ValidCode } from '../../components';
import { getRequestUrl, verifyPhone } from '../../utils';
import { globalState } from '../../models';

interface IPhoneChangePane {
    mode: VALID_STEP; // 验证步骤
    validType: VALID_TYPE; // 验证内容
    setValidType: (validType: VALID_TYPE) => void;
    setCodeValid: React.Dispatch<React.SetStateAction<boolean>>;
    userInfo: {
        email?: string;
        phone?: string;
        is_email_verified?: boolean;
    };
    codeValid: boolean;
    visible: boolean;
    onClose?: () => void;
    availableValidateType: string[];
    onModifySuccess: (
        phoneValue: string,
        value: string // 验证码或者密码
    ) => void | Promise<any>;
}

const codeVerify = (codeValue: string) => {
    return !!codeValue;
};

const passwordVerify = (password: string) => {
    return password.length >= 6;
};

const hasType = (type: 'pwd' | 'email' | 'phone', availableValidateType: string[]) => {
    return includes(availableValidateType, type);
};

const { EMAIL } = UGC_MAX_LENGTH.USER;

const VerifyChangePane: React.FunctionComponent<IPhoneChangePane> = (props) => {
    const {
        mode,
        userInfo,
        validType,
        visible,
        codeValid,
        onClose = EMPTY_FN,
        setValidType,
        setCodeValid,
        availableValidateType,
        onModifySuccess
    } = props;
    const phoneDelimiter = '-';
    const { email, phone = '', is_email_verified } = userInfo;
    const [phoneValid, setPhoneValid] = useState<boolean>(true);
    const [emailValid, setEmailValid] = useState<boolean>(true);
    const [phoneValue, setPhoneValue] = useState<string>('');
    const [emailValue, setEmailValue] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [isPasswordValid, setPasswordValid] = useState<boolean>(true);
    const [codeValue, setCodeValue] = useState<string>('');
    const [captchaDialogVisible, setCaptchaDialogVisible] = useState<boolean>(false); // 是否显示图片验证码
    const [captchaData, setCaptchaData] = useState<Record<string, string>>({ phone }); // 图片验证码手机号参数
    const validCodeRef = useRef<any>();
    const isMobile = globalState.isMobile; // 是否是移动端布局

    useEffect(() => {
        // 跳到下一步时清除倒计时状态
        if (validCodeRef.current) {
            validCodeRef.current.clearReloadState();
        }
        setCodeValue('');
        setPhoneValue('');
        setEmailValue('');
        setPassword('');
        setCodeValid(true);
    }, [mode, validType, visible]);

    useEffect(() => {
        if (validType === VALID_TYPE.EMAIL) {
            setCaptchaData({ email: emailValue });
        }
    }, [emailValue]);

    useEffect(() => {
        if (validType === VALID_TYPE.PHONE) {
            setCaptchaData({ phone: phoneValue });
        }
    }, [phoneValue]);

    const getDialogTitle = (mode: VALID_STEP) => {
        switch (mode) {
            case VALID_STEP.BIND:
            case VALID_STEP.BIND2:
                return platformIntl.get('phone.bind');
            case VALID_STEP.MFA:
                return platformIntl.get('valid.code.emptyTip');
            default:
                return '';
        }
    };

    const emailVerify = () => {
        return new RegExp(TEXT_TYPE_REGEXP.EMAIL).test(emailValue);
    };

    const emailOverLimit = () => {
        return size(trim(emailValue)) > EMAIL;
    };

    const renderFooter = () => {
        switch (mode) {
            case VALID_STEP.BIND: {
                const className = isMobile ? 'text-blue' : 'text-primary';
                if (
                    includes([VALID_TYPE.EMAIL, VALID_TYPE.PHONE], validType) &&
                    includes(availableValidateType, 'pwd')
                ) {
                    return (
                        <div className="line-footer">
                            <span>
                                {validType === VALID_TYPE.PHONE
                                    ? platformIntl.get('phone.disable.question')
                                    : platformIntl.get('email.disable.question')}
                            </span>
                            <span
                                onClick={() => {
                                    setValidType(VALID_TYPE.PWD);
                                }}
                                className={className}
                            >
                                {platformIntl.get('password.validate')}
                            </span>
                        </div>
                    );
                }
                if (
                    isMobile &&
                    validType === VALID_TYPE.PWD &&
                    includes(availableValidateType, VALID_TYPE.EMAIL)
                ) {
                    return (
                        <div className="line-footer">
                            <span>{platformIntl.get('password.forget')}</span>
                            <span
                                onClick={() => {
                                    setValidType(VALID_TYPE.EMAIL);
                                }}
                                className={className}
                            >
                                {platformIntl.get('code.validate')}
                            </span>
                        </div>
                    );
                }
                break;
            }
            default:
                return null;
        }
    };

    // 根据类型和步骤加载内容
    const renderContentArrayByType = () => {
        switch (mode) {
            case VALID_STEP.BIND:
                switch (validType) {
                    case VALID_TYPE.EMAIL:
                        return ['tip', 'email_disabled', 'email_validate'];
                    case VALID_TYPE.PWD:
                        if (hasType('email', availableValidateType)) {
                            return ['tip', 'email_disabled', 'pwd'];
                        } else {
                            return ['tip', 'pwd'];
                        }
                    case VALID_TYPE.PHONE:
                        return ['tip', 'phone_disabled', 'phone_validate'];
                }
                break;
            case VALID_STEP.BIND2:
                switch (validType) {
                    case 'email':
                        return ['email', 'email_validate'];
                    case 'phone':
                        return ['phone', 'phone_validate'];
                }
                break;
            case VALID_STEP.MFA:
                switch (validType) {
                    case 'email':
                        return ['tip', 'email_disabled', 'email_validate'];
                    case 'phone':
                        return ['tip', 'phone_disabled', 'phone_validate'];
                }
                break;
        }
    };

    /**
     * 验证码点击
     *
     * @param validType
     */
    const dealValidCodeBtnClick = (validType: 'email' | 'phone') => {
        if (validType === 'phone') {
            if (!includes([VALID_STEP.BIND2], mode)) {
                if (isEmpty(phone)) {
                    warningToast(platformIntl.get('phone.invalid.warning'));
                    return;
                }
                const data: any = {
                    phone: phone
                };
                if (mode !== VALID_STEP.MFA) {
                    data.check_current_phone = true;
                }
                return getPhone(data)
                    .then(() => {
                        validCodeRef.current.setReloadState();
                    })
                    .catch((err) => {
                        const errCode = get(err, 'response.data.code');
                        if (ECode.USER_NO_AUTH_CODE_CAPTCHA === errCode) {
                            setCaptchaData({ phone: data.phone });
                            setCaptchaDialogVisible(true);
                        }
                    });
            }
            if (!phoneValue) {
                warningToast(platformIntl.get('phone.invalid.warning'));
                return;
            }
            // 是否校验当前手机
            const data: any = {
                phone: phoneValue
            };
            return getPhone(data)
                .then(() => {
                    validCodeRef.current.setReloadState();
                })
                .catch((err) => {
                    const errCode = get(err, 'response.data.code');
                    if (ECode.USER_NO_AUTH_CODE_CAPTCHA === errCode) {
                        setCaptchaData({ phone: data.phone });
                        setCaptchaDialogVisible(true);
                    }
                });
        } else {
            if (!includes([VALID_STEP.BIND2], mode)) {
                if (isEmpty(email)) {
                    warningToast(platformIntl.get('email.invalid.warning'));
                    return;
                }
                const data = {
                    email: email,
                    check_current_email: is_email_verified
                };
                return getEmail(data)
                    .then(() => {
                        validCodeRef.current.setReloadState();
                    })
                    .catch((err) => {
                        const errCode = get(err, 'response.data.code');
                        if (ECode.USER_NO_AUTH_CODE_CAPTCHA === errCode) {
                            setCaptchaData({ email: data.email || '' });
                            setCaptchaDialogVisible(true);
                        }
                    });
            }
            if (!emailValue) {
                warningToast(platformIntl.get('email.invalid.warning'));
                return;
            }
            if (emailOverLimit()) {
                return;
            }

            return getEmail({ email: emailValue })
                .then(() => {
                    validCodeRef.current.setReloadState();
                })
                .catch((err) => {
                    const errCode = get(err, 'response.data.code');
                    if (ECode.USER_NO_AUTH_CODE_CAPTCHA === errCode) {
                        setCaptchaData({ email: emailValue });
                        setCaptchaDialogVisible(true);
                    }
                });
        }
    };

    const getText4Ok = () => {
        switch (mode) {
            case VALID_STEP.BIND:
                return platformIntl.get('nextStep');
            case VALID_STEP.MFA:
                return platformIntl.get('ok');
            default:
                return platformIntl.get('save');
        }
    };

    const warningToast = (text: string) => {
        Message.add({
            messageType: 'toast',
            type: 'warning',
            message: text
        });
    };

    const handleDialogOnOk = () => {
        switch (validType) {
            case VALID_TYPE.PHONE:
                if (includes([VALID_STEP.BIND2], mode) && !verifyPhone(phoneValue, true)) {
                    setPhoneValid(false);
                    warningToast(platformIntl.get('phone.invalid.warning'));
                    break;
                }
                setCodeValid(codeVerify(codeValue));
                if (isEmpty(codeValue)) {
                    warningToast(platformIntl.get('phone.check.required'));
                } else {
                    return onModifySuccess(phoneValue, codeValue);
                }
                break;
            case VALID_TYPE.EMAIL:
                if (includes([VALID_STEP.BIND2], mode)) {
                    if (emailOverLimit()) {
                        break;
                    } else if (!emailVerify()) {
                        setEmailValid(false);
                        warningToast(platformIntl.get('email.invalid.warning'));
                        break;
                    }
                }
                setCodeValid(codeVerify(codeValue));
                if (isEmpty(codeValue)) {
                    warningToast(platformIntl.get('email.check.required'));
                    break;
                }
                return onModifySuccess(emailValue, codeValue);
            case VALID_TYPE.PWD:
                setPasswordValid(passwordVerify(password));
                if (isEmpty(password)) {
                    warningToast(platformIntl.get('password.emptyTip'));
                    break;
                }
                return onModifySuccess('', password);
        }
    };

    const renderLineContent = (type: string, i: number) => {
        const inputHeight = isMobile ? 40 : 32;
        switch (type) {
            case 'tip':
                return (
                    <div key={i} className="line-content">
                        <span className="line-content-tip">
                            {mode === VALID_STEP.MFA
                                ? platformIntl.get('login.mfa.desc')
                                : platformIntl.get('safe.verify.check')}
                        </span>
                    </div>
                );
            case 'email':
                return (
                    <div key={i} className="line-content">
                        <div className="line-label">{platformIntl.get('email.new')}</div>
                        <ErrorInput
                            errMsg={
                                emailOverLimit()
                                    ? platformIntl.get('max.length.error.message', { max: EMAIL })
                                    : ''
                            }
                            style={{ width: '100%', height: `${inputHeight}px` }}
                            value={emailValue}
                            inputState={!emailOverLimit() && emailValid ? 'normal' : 'error'}
                            onChange={(value) => {
                                setEmailValue(value);
                            }}
                            onBlur={() => {
                                setEmailValid(emailVerify());
                            }}
                        />
                    </div>
                );
            case 'email_disabled':
                return (
                    <div key={i} className="line-content">
                        <div className="line-label">{platformIntl.get('email.current')}</div>
                        <Input
                            style={{ width: '100%', height: `${inputHeight}px` }}
                            value={email}
                            disabled
                        />
                    </div>
                );
            case 'email_validate':
            case 'phone_validate':
                return (
                    <div key={i} className="line-content">
                        <div className="line-label">{platformIntl.get('verify.code')}</div>
                        <ValidCode
                            ref={validCodeRef}
                            codeValid={codeValid}
                            onChange={(value) => {
                                setCodeValue(value);
                            }}
                            waterMark={
                                type === 'email_validate'
                                    ? platformIntl.get('email.verify.code')
                                    : platformIntl.get('phone.verify.code')
                            }
                            onBtnClick={() => {
                                dealValidCodeBtnClick(
                                    type === 'email_validate' ? 'email' : 'phone'
                                );
                            }}
                            onInputBlur={() => {
                                setCodeValid(!!codeValue);
                            }}
                            height={inputHeight}
                        />
                    </div>
                );
            case 'pwd':
                return (
                    <div key={i} className="line-content">
                        <div className="line-label">{platformIntl.get('password.signIn')}</div>
                        <Input
                            className="password-input"
                            style={{ width: '100%', height: `${inputHeight}px` }}
                            value={password}
                            type="password"
                            inputState={isPasswordValid ? 'normal' : 'error'}
                            onChange={(val) => {
                                setPassword(val);
                            }}
                            onBlur={(v) => {
                                setPasswordValid(passwordVerify(v));
                            }}
                            placeholder={platformIntl.get('password')}
                        />
                    </div>
                );
            case 'phone':
                return (
                    <div key={i} className="line-content">
                        <div className="line-label">{platformIntl.get('phone.new')}</div>
                        <I18nPhone
                            phoneValid={phoneValid}
                            disabled={false}
                            value={phoneValue}
                            height={inputHeight}
                            phoneDelimiter={phoneDelimiter}
                            onBlur={(value) => {
                                setPhoneValid(verifyPhone(value, true));
                                setPhoneValue(value);
                            }}
                        />
                    </div>
                );
            case 'phone_disabled':
                return (
                    <div key={i} className="line-content">
                        <div className="line-label">{platformIntl.get('phone.current')}</div>
                        <I18nPhone
                            phoneValid={true}
                            disabled={true}
                            value={phone}
                            height={inputHeight}
                            phoneDelimiter={phoneDelimiter}
                        />
                    </div>
                );
        }
    };

    return (
        <>
            {isMobile ? (
                <Drawer
                    className="fx-operate-drawer"
                    visible={visible}
                    title={getDialogTitle(mode)}
                    onClose={() => {
                        onClose();
                    }}
                    edge={{ height: 400 }}
                    closable={false}
                    autoClose
                    placement="bottom"
                >
                    {map(renderContentArrayByType(), (v, i) => {
                        return renderLineContent(v, i);
                    })}
                    {renderFooter()}
                    <div className="fx-operate-drawer-footer">
                        <Button className="drawer-btn btn-cancel" onClick={onClose}>
                            {platformIntl.get('cancel')}
                        </Button>
                        <Button className="drawer-btn btn-ok" onClick={handleDialogOnOk}>
                            {getText4Ok()}
                        </Button>
                    </div>
                </Drawer>
            ) : (
                <Dialog
                    className="fx-operate-dialog"
                    title={getDialogTitle(mode)}
                    visible={visible}
                    width={440}
                    height={350}
                    text4Ok={getText4Ok()}
                    separator={false}
                    footer={renderFooter()}
                    onCancel={() => {
                        onClose();
                    }}
                    onOk={() => {
                        handleDialogOnOk();
                    }}
                >
                    {map(renderContentArrayByType(), (v, i) => {
                        return renderLineContent(v, i);
                    })}
                </Dialog>
            )}
            <CaptchaDialog
                visible={captchaDialogVisible}
                url={
                    !isNil(captchaData.email)
                        ? getRequestUrl(API.authentication.send_email)
                        : getRequestUrl(API.authentication.send_sms)
                }
                data={captchaData}
                onClose={() => {
                    setCaptchaDialogVisible(false);
                }}
                onSuccess={() => {
                    setCaptchaDialogVisible(false);
                    validCodeRef.current.setReloadState();
                }}
            />
        </>
    );
};

export default VerifyChangePane;
